import { useState, useSyncExternalStore } from "react";
import { useDebounce } from "../hooks/useDebounce.ts";
import { useSearch } from "../hooks/useSearch.ts";
import { useDocumentMeta } from "../hooks/useDocumentMeta.ts";
import { ProjectGrid } from "../components/ProjectGrid.tsx";
import { SearchSkeleton } from "../components/SearchSkeleton.tsx";
import { navigate, subscribeSearchParams, getSearchQuery } from "../lib/router.ts";

function useUrlQuery() {
  return useSyncExternalStore(subscribeSearchParams, getSearchQuery, () => "");
}

export function SearchResultsPage({ query: initialQuery }: { query: string }) {
  // Live query from URL (updated by header SearchBar via replaceUrl)
  const urlQuery = useUrlQuery() || initialQuery;
  const debouncedQuery = useDebounce(urlQuery, 250);
  const [page, setPage] = useState(1);
  const { results, matchedCategories, meta, loading } = useSearch(debouncedQuery, page);

  useDocumentMeta({
    title: urlQuery ? `Search: ${urlQuery} | There's an OS for That` : "Search | There's an OS for That",
    description: urlQuery ? `Search results for "${urlQuery}" on There's an OS for That.` : undefined,
  });

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <a
        href="/"
        onClick={(e) => { e.preventDefault(); navigate("/"); }}
        className="text-xs text-text-muted hover:text-accent transition-colors mb-4 inline-block"
      >
        &larr; back
      </a>

      {/* Matched Categories */}
      {matchedCategories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {matchedCategories.map((cat) => (
            <a
              key={cat.id}
              href={`/category/${cat.slug}`}
              onClick={(e) => {
                e.preventDefault();
                navigate(`/category/${cat.slug}`);
              }}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] border border-border bg-surface hover:border-accent/50 hover:text-accent transition-colors"
            >
              <span>{cat.icon}</span>
              <span>{cat.name}</span>
            </a>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3 mb-6">
        <h1 className="text-sm text-text-primary">
          results for <span className="text-accent">"{urlQuery}"</span>
        </h1>
        {!loading && (
          <span className="text-xs text-text-muted tabular-nums">{meta.total_projects} found</span>
        )}
        {meta.mode === "semantic" && !loading && (
          <span className="text-[10px] text-text-muted/60 border border-border/50 px-1.5 py-0.5">semantic</span>
        )}
      </div>

      {loading ? (
        <SearchSkeleton rows={8} />
      ) : (
        <ProjectGrid projects={results} />
      )}
    </div>
  );
}
